import { CBadge, CButton, CFormLabel, CFormTextarea, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle } from '@coreui/react'
import { Plus, Edit2, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import Editor, { Toolbar } from 'react-simple-wysiwyg'

const PatientRisk = ({ diagnosisName, diagnosisAddedPinger, riskfactors, selectedRisks, setSelectedRisks, patientData, noteStructureRef, hospitalRecords, personalHistory, riskFactorWithKeyword, saveTreatsShell, setRiskFactors, patientTreatments, noteID }) => {
  const [visible, setVisible] = useState(false)
  const [textInput, setTextInput] = useState('')
  const [extraKeywords, setExtraKeywords] = useState([])

  const [showEditHtml, setShowEditHtml] = useState(false)
  const [tempHtmlContent, setTempHtmlContent] = useState('')
  const handleInputChange = (e) => {
    setTextInput(e.target.value)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const trimmed = textInput.trim()
      if (trimmed && !extraKeywords.includes(trimmed)) {
        setExtraKeywords((prev) => [...prev, trimmed])

        setTextInput('')
      }
    }
  }

  const handleSave = () => {
    setRiskFactors((prev) => {
      const currentList = prev[diagnosisName] || []
      const newKeywords = extraKeywords.filter((keyword) => !currentList.includes(keyword))

      if (newKeywords.length === 0) return prev

      return {
        ...prev,
        [diagnosisName]: [...currentList, ...newKeywords],
      }
    })

    extraKeywords.forEach((keyword) => {
      toggleRiskSelection(keyword, diagnosisName)
    })

    setExtraKeywords([])
    setVisible(false)
  }

  const handleEditKeyword = (index) => {
    const keywordToEdit = extraKeywords[index]
    setTextInput(keywordToEdit)
    setExtraKeywords((prev) => prev.filter((_, i) => i !== index))
  }

  const handleRemoveKeyword = (index) => {
    const keywordToRemove = extraKeywords[index]

    setExtraKeywords((prev) => prev.filter((_, i) => i !== index))

    setRiskFactors((prev) => {
      const updatedDiagnosisRisk = (prev[diagnosisName] || []).filter((item) => item !== keywordToRemove)

      return {
        ...prev,
        [diagnosisName]: updatedDiagnosisRisk,
      }
    })
  }

  const extractText = (records) => {
    return records
      .map((item) => Object.values(item).join(' '))
      .join(' ')
      .toLowerCase()
  }

  useEffect(() => {
    let allText = extractText(hospitalRecords) + ' ' + extractText(personalHistory.pmhx) + ' ' + extractText(personalHistory.pshx) + ' ' + extractText(personalHistory.fh) + ' ' + extractText(personalHistory.sh)

    let updatedRisks = { ...selectedRisks }

    Object.entries(riskFactorWithKeyword).forEach(([_, riskData]) => {
      if (riskData?.length > 0) {
        riskData.forEach((risk) => {
          if (risk?.keywords.some((keyword) => allText.includes(keyword.toLowerCase()))) {
            const existingRisks = new Set(updatedRisks[diagnosisName] ? updatedRisks[diagnosisName].split(',') : [])
            existingRisks.add(risk.riskName)
            updatedRisks[diagnosisName] = Array.from(existingRisks).join(',')
          }
        })
      }
    })

    setSelectedRisks(updatedRisks)
  }, [hospitalRecords, personalHistory, diagnosisName, riskFactorWithKeyword])

  const toggleRiskSelection = (risk, diagnosisName) => {
    setSelectedRisks((prevState) => {
      const currentRisksRaw = prevState[diagnosisName] ? prevState[diagnosisName].split(',') : []

      // Trim each risk to normalize the entries
      const currentRisks = currentRisksRaw.map((r) => r.trim())

      // Also trim the incoming risk for comparison
      const trimmedRisk = risk.trim()

      const isSelected = currentRisks.includes(trimmedRisk)

      const updatedRisks = isSelected ? currentRisks.filter((r) => r !== trimmedRisk) : [...currentRisks, trimmedRisk]

      return {
        ...prevState,
        [diagnosisName]: updatedRisks.join(','),
      }
    })
  }

  useEffect(() => {
    if (!diagnosisName) return

    // const existingRisks = noteStructureRef.current.riskFactors?.[diagnosisName] || '';
    const newRisks = selectedRisks?.[diagnosisName] || ''

    // // Convert both to arrays (split by `,`) and clean them
    // const existingRiskArr = existingRisks ? existingRisks.split(',').map(r => r.trim()) : [];
    // const newRiskArr = newRisks ? newRisks.split(',').map(r => r.trim()) : [];
    // // Merge without duplicates
    // const mergedRiskSet = new Set([...existingRiskArr, ...newRiskArr.filter(Boolean)]);
    // const mergedRisks = Array.from(mergedRiskSet).join(',');

    // Update riskFactors
    noteStructureRef.current = {
      ...noteStructureRef.current,
      riskFactors: {
        ...noteStructureRef.current.riskFactors,
        [diagnosisName]: newRisks,
      },
    }
  }, [diagnosisName, selectedRisks])

  // Add the risk 65 advance if more than 65

  useEffect(() => {
    if (patientData?.Age && parseInt(patientData.Age) > 65) {
      const selectedList = selectedRisks[diagnosisName]?.split(',').map((r) => r.trim())

      for (const [diagnosisName, riskItems] of Object.entries(riskfactors)) {
        const hasAdvancedAge = riskItems.includes('Advanced age greater than 65')

        const isDiagnosisInSelected = selectedList?.includes(diagnosisName)

        if (hasAdvancedAge && !isDiagnosisInSelected) {
          toggleRiskSelection('Advanced age greater than 65', diagnosisName)
        }
      }
    } else {
      console.log('Patient is underage')
    }
  }, [diagnosisAddedPinger])

  useEffect(() => {
    const currentRiskString = noteStructureRef?.current?.riskFactors?.[diagnosisName] || ''
    const currentDiagnosisRiskArrayInNoteStructure = currentRiskString
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

    const existingRiskFactors = riskfactors?.[diagnosisName] || []

    const newItems = currentDiagnosisRiskArrayInNoteStructure.filter((item) => !existingRiskFactors.includes(item))

    if (newItems.length > 0) {
      setRiskFactors((prev) => ({
        ...prev,
        [diagnosisName]: [...existingRiskFactors, ...newItems],
      }))
    }
  }, [noteStructureRef, riskfactors])

  return (
    <>
      <span className="d-flex align-items-center gap-2">
        <div>
          <b className="">Risk Factors </b>
        </div>
        <CFormLabel
          className="patient-note-action d-flex mt-2 flex-column align-items-center justify-content-center shadow"
          onClick={() => setVisible((p) => !p)}
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '20px',
            background: 'var(--ui-color-primary, #2b7cd3)',
            cursor: 'pointer',
          }}
        >
          <Plus className="patient-note-action-icon" strokeWidth={2} size={15} />
        </CFormLabel>
      </span>
     {/* {noteStructureRef?.current?.freeTypesEverything?.riskfactors?.[diagnosisName] && (
  <>
    <div className="mt-2">
      <div
        className="p-4"
        style={{
          background: '#f5f5f5',
          borderRadius: '10px',
          border: '1px solid #dee2e6',
          color: '#6c757d',
          cursor: 'not-allowed',
          pointerEvents: 'none',
          opacity: 0.8,
          // userSelect: 'none' // optional
        }}
        dangerouslySetInnerHTML={{
          __html:
            noteStructureRef?.current?.freeTypesEverything?.riskfactors?.[diagnosisName],
        }}
      ></div>
    </div>
    <hr />
  </>
)} */}
{noteStructureRef?.current?.freeTypesEverything?.riskfactors?.[diagnosisName] && (
  <>
    <div className="mt-2">
      <div
        className="p-2"
        style={{
          background: 'inherit',
          borderRadius: '10px',
          border: '1px solid gray',
          color: 'inherit',
          cursor: 'not-allowed',
          pointerEvents: 'none',
          opacity: 0.8,
          // userSelect: 'none' // optional
        }}
        dangerouslySetInnerHTML={{
          __html:
            noteStructureRef?.current?.freeTypesEverything?.riskfactors?.[diagnosisName],
        }}
      ></div>
    </div>
    <hr />
  </>
)}
      <div style={{ marginLeft: '1rem' }}>
        {riskfactors[diagnosisName]?.map((risk, index) => {
          const isSelected = selectedRisks[diagnosisName]?.includes(risk)

          return (
            <CButton
              variant="outline"
              key={index}
              color="primary"
              onClick={() => toggleRiskSelection(risk, diagnosisName)}
              className={`m-1 patient-risk-pill ${isSelected ? 'patient-risk-pill--selected' : ''}`}
            >
              {risk}
            </CButton>
          )
        })}
      </div>

      <CModal
        className="patient-theme-modal patient-note-form-modal"
        alignment="center"
        visible={visible}
        backdrop="static"
        onClose={() => {
          setVisible(false)
          setExtraKeywords([])
          setTextInput('')
        }}
        aria-labelledby="VerticallyCenteredExample"
      >
        <CModalHeader>
          <CModalTitle id="VerticallyCenteredExample">Add Risk Factors to {diagnosisName}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormTextarea
            className="patient-note-form-control"
            placeholder="Type and press enter to add custom keywords"
            value={textInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            rows={1}
          />
          <div className="mt-2">
            {extraKeywords?.map((keyword, index) => (
              <CBadge
                key={index}
                color="primary"
                className="me-1 p-2"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  width: 'fit-content',
                }}
              >
                <span style={{}}>{keyword}</span>
                <Edit2
                  size={12}
                  style={{
                    cursor: 'pointer',
                    color: 'yellow',
                    marginLeft: '5px',
                    marginRight: '2px',
                  }}
                  onClick={() => handleEditKeyword(index)}
                />
                <X className="text-white" size={12} style={{ cursor: 'pointer', color: 'red' }} onClick={() => handleRemoveKeyword(index)} />
              </CBadge>
            ))}
          </div>
        </CModalBody>
        <CModalFooter className="p-4 m-0">
          <CButton
            variant="outline"
            color="secondary"
            onClick={() => {
              setVisible(false)
              setExtraKeywords([])
              setTextInput('')
            }}
          >
            Close
          </CButton>
          <CButton
            color="primary"
            onClick={() => {
              handleSave()
              setTextInput('')
            }}
          >
            Save
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal className="patient-theme-modal patient-note-form-modal" size="lg" visible={showEditHtml} onClose={() => setShowEditHtml(false)} aria-labelledby="OptionalSizesExample2">
        <CModalHeader>
          <CModalTitle id="OptionalSizesExample2">Edit Risk Factor Free Type</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <Editor
            onChange={(e) => {
              setTempHtmlContent(e.target.value)
            }}
            containerProps={{
              style: {
                width: '100%',
                background: '#f9f9f9',
                border: 0,
                height: '100%',
                minHeight: 'unset',
              },
            }}
            value={tempHtmlContent}
          >
            <tooltip />
          </Editor>
        </CModalBody>
        <CModalFooter>
          <CButton variant="outline" color="secondary" onClick={() => setShowEditHtml(false)}>
            Close
          </CButton>
          <CButton
            color="primary"
            onClick={() => {
              noteStructureRef.current = {
                ...noteStructureRef.current,
                freeTypesEverything: {
                  ...noteStructureRef.current?.freeTypesEverything,
                  riskfactors: {
                    ...noteStructureRef?.current?.freeTypesEverything?.riskfactors,
                    [diagnosisName]: tempHtmlContent,
                  },
                },
              }
              saveTreatsShell(false)
              setShowEditHtml(false)
            }}
          >
            Save
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default PatientRisk
